// File: client/src/components/LoanApplicationActionsMenu.jsx

import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import Loan360Modal from "./Loan360Modal";

const APPROVE_REJECT_LOAN = gql`
  mutation ApproveRejectLoan($record: LoanApplicationsInput!) {
    approveRejectLoanApplication(record: $record) {
      data
      errorMessage
      status
    }
  }
`;

const GET_GUARANTORS_BY_LOAN_ID = gql`
  query GetGuarantors($loanApplicationId: Int!) {
    loanGuarantorsByLoanId(loanApplicationId: $loanApplicationId) {
      amountPaidBack
      amountPledged
      applicantUserId
      approvedByGuarantor
      createdBy
      createdDate
      expectedInterestShare
      firstName
      guarantorId
      id
      lastName
      loanApplicationId
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_LOAN_DISBURSEMENT = gql`
  mutation CreateLoanDisbursement($input: CreateLoanDisbursementInput!) {
    createLoanDisbursement(input: $input) {
      data
      errorMessage
      isSuccess
    }
  }
`;

const LoanApplicationActionsMenu = ({ loan, onActionComplete, canUploadDisbursement = true }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reasonForRejection, setReasonForRejection] = useState("");
  const [disbursementDialogOpen, setDisbursementDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [expectedAmount, setExpectedAmount] = useState(loan?.requestedAmount || "");
  const [showLoan360, setShowLoan360] = useState(false);
  const [loanWithGuarantors, setLoanWithGuarantors] = useState(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info", // "success" | "error" | "warning" | "info"
  });

  const [approveReject] = useMutation(APPROVE_REJECT_LOAN);
  const [createLoanDisbursement, { loading: uploadingDisbursement }] =
    useMutation(CREATE_LOAN_DISBURSEMENT);
  const [fetchGuarantors] = useLazyQuery(GET_GUARANTORS_BY_LOAN_ID);

  const showSnack = (message, severity = "info") => {
    setSnack({ open: true, message, severity });
  };

  const closeSnack = () => {
    setSnack((s) => ({ ...s, open: false }));
  };

  const extractApolloErrorMessage = (err) => {
    // GraphQL errors
    const gqlMsg = err?.graphQLErrors?.[0]?.message;
    if (gqlMsg) return gqlMsg;

    // Network/server errors (sometimes GraphQL returns errors inside networkError.result)
    const netMsg =
      err?.networkError?.result?.errors?.[0]?.message ||
      err?.networkError?.message;

    if (netMsg) return netMsg;

    return err?.message || "Something went wrong.";
  };

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApprove = async () => {
    handleClose();
    await submitAction(3);
  };

  const handleReject = () => {
    handleClose();
    setRejectDialogOpen(true);
  };

  const handleCancel = async () => {
    handleClose();
    await submitAction(6);
  };

  const submitAction = async (statusId) => {
    try {
      const { data } = await approveReject({
        variables: {
          record: {
            id: loan.id,
            statusId,
            reasonForRejection: statusId === 5 ? reasonForRejection : null,
          },
        },
      });

      const result = data?.approveRejectLoanApplication;

      if (result?.status === "Success") {
        showSnack(
          statusId === 3
            ? "Approved successfully."
            : statusId === 5
            ? "Rejected successfully."
            : "Cancelled successfully.",
          "success"
        );

        onActionComplete?.();
      } else {
        showSnack(result?.errorMessage || "Operation failed.", "error");
      }
    } catch (err) {
      console.error(err);
      showSnack(extractApolloErrorMessage(err), "error");
    }
  };

  const handleLoan360 = async () => {
    handleClose();

    try {
      const { data } = await fetchGuarantors({
        variables: { loanApplicationId: loan.id },
      });

      const guarantors = data?.loanGuarantorsByLoanId || [];
      setLoanWithGuarantors({ ...loan, guarantors });
      setShowLoan360(true);
    } catch (err) {
      console.error(err);
      showSnack(extractApolloErrorMessage(err), "error");
    }
  };

  const handleOpenDisbursementDialog = () => {
    handleClose();
    setExpectedAmount(loan?.requestedAmount || "");
    setDisbursementDialogOpen(true);
  };

  const handleSubmitDisbursement = async () => {
    try {
      const parsedAmount = Number(expectedAmount);
      const { data } = await createLoanDisbursement({
        variables: {
          input: {
            confirmationMessage: confirmationMessage.trim(),
            loanApplicationId: loan.id,
            expectedAmount: parsedAmount,
          },
        },
      });

      const result = data?.createLoanDisbursement;

      if (result?.isSuccess) {
        showSnack("Disbursement confirmation uploaded successfully.", "success");
        setDisbursementDialogOpen(false);
        setConfirmationMessage("");
        onActionComplete?.();
      } else {
        showSnack(result?.errorMessage || "Failed to upload disbursement confirmation.", "error");
      }
    } catch (err) {
      console.error(err);
      showSnack(extractApolloErrorMessage(err), "error");
    }
  };

  const isDisbursementFormValid = confirmationMessage.trim() && Number(expectedAmount) > 0;

  return (
    <>
      <IconButton onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleApprove}>
          <ListItemIcon>
            <CheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Approve</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleReject}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reject</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleCancel}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleLoan360}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Loan 360</ListItemText>
        </MenuItem>

        {canUploadDisbursement && (
          <MenuItem onClick={handleOpenDisbursementDialog}>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload Disbursement Confirmation</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Reject Loan Application</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for Rejection"
            fullWidth
            multiline
            rows={3}
            value={reasonForRejection}
            onChange={(e) => setReasonForRejection(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setRejectDialogOpen(false);
              await submitAction(5);
            }}
            disabled={!reasonForRejection.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disbursementDialogOpen}
        onClose={() => setDisbursementDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Disbursement Confirmation</DialogTitle>
        <DialogContent>
          <TextField
            label="Expected Amount"
            type="number"
            fullWidth
            margin="normal"
            value={expectedAmount}
            onChange={(e) => setExpectedAmount(e.target.value)}
          />

          <TextField
            label="Confirmation Message"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            value={confirmationMessage}
            onChange={(e) => setConfirmationMessage(e.target.value)}
            placeholder="Paste the disbursement confirmation SMS/message here"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisbursementDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitDisbursement}
            disabled={!isDisbursementFormValid || uploadingDisbursement}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Loan360Modal
        open={showLoan360}
        onClose={() => setShowLoan360(false)}
        loan={loanWithGuarantors}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoanApplicationActionsMenu;
